/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  styled,
  TablePagination,
  TextField,
  Box,
  TableSortLabel,
  InputAdornment,
  Chip,
  Link,
  useMediaQuery,
  Collapse,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import './Listado.scss';
import SavedSearchIcon from '@mui/icons-material/SavedSearch';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { theme } from 'theme/theme';
import { ArrowLeft, ArrowRight, FilterList, ReplyAll } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Tooltip from '@mui/material/Tooltip';
import Spinner from '../Spinner/Spinner';
import { anchoMaxForms } from 'utils/constants';
import { useDispatch } from 'react-redux';
import { setListado } from 'utils/redux/features/UISlice';

export interface HeadCell<T> {
  Id: keyof T;
  label: string;
  numeric: boolean;
  disablePadding: boolean;
}

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.primary.contrastText,
  },
}));

const Listado = <T extends { Id: number }>({
  lista,
  headCells,
  titulo,
  onAgregar,
  onEliminar,
  onEditar,
  onSeleccionar,
  onFiltrar,
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  onLimpiarFechas,
  loading,
}: {
  lista: T[];
  headCells: HeadCell<T>[];
  titulo: string;
  onAgregar?: () => void;
  onEliminar?: (id: number) => void;
  onEditar?: (id: number) => void;
  onFiltrar?: (event: React.FormEvent<HTMLFormElement>) => void;
  onLimpiarFechas?: any;
  startDate?: string;
  setStartDate?: (date: string) => void;
  setEndDate?: (date: string) => void;
  endDate?: string;
  onSeleccionar?: (ids: number[]) => void;
  loading: boolean;
}) => {
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = useState<keyof T>('Id');
  const [selected, setSelected] = useState<number[]>([]);
  const [page, setPage] = useState(0);
  const [filasPorPagina, setFilasPorPagina] = useState(10);
  const [filtros, setFiltros] = useState<{ [key: string]: string }>({});
  const [collapsedColumns, setCollapsedColumns] = useState<string[]>([]);
  const navigate = useNavigate();
  const handleChangePage = (e: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleCollapse = (columnId: string) => {
    if (collapsedColumns.includes(columnId)) {
      setCollapsedColumns(collapsedColumns.filter(col => col !== columnId));
    } else {
      setCollapsedColumns([...collapsedColumns, columnId]);
    }
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilasPorPagina(+event.target.value);
    setPage(0);
  };

  const MiniaturaFoto = ({ src }: { src: string }) => (
    <Avatar alt="Miniatura" src={src} sx={{ width: 56, height: 56, mx: 'auto' }} />
  );

  const handleRequestSort = (property: keyof T) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleClick = (event: React.MouseEvent<unknown>, Id: number) => {
    const selectedIndex = selected.indexOf(Id);
    let newSelected: number[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, Id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
    }
    setSelected(newSelected);
  };

  const isSelected = (id: number) => selected.indexOf(id) !== -1;

  const sortData = (array: T[], order: 'asc' | 'desc', orderBy: keyof T) => {
    return array.sort((a, b) => {
      const aValue = a[orderBy];
      const bValue = b[orderBy];

      const isDate = (dateString: any) => {
        return typeof dateString === 'string' && /^\d{2}-\d{2}-\d{4}$/.test(dateString);
      };

      if (isDate(aValue) && isDate(bValue)) {
        // Convierte las fechas string a objetos Date
        const [dayA, monthA, yearA] = (aValue as string).split('-').map(Number);
        const [dayB, monthB, yearB] = (bValue as string).split('-').map(Number);
        const dateA = new Date(yearA, monthA - 1, dayA);
        const dateB = new Date(yearB, monthB - 1, dayB);

        // Compara fechas
        if (dateA < dateB) return order === 'asc' ? -1 : 1;
        if (dateA > dateB) return order === 'asc' ? 1 : -1;
        return 0;
      }

      // Comparación normal si no es fecha
      if (bValue < aValue) return order === 'asc' ? -1 : 1;
      if (bValue > aValue) return order === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const sortedList = sortData([...lista], order, orderBy);

  const filteredList = sortedList.filter(row => {
    return headCells.every(headCell => {
      const celdaValor = (row as any)[headCell.Id];
      const filtroValor = filtros[headCell.Id as string] || '';
      return celdaValor != null && celdaValor.toString().toLowerCase().includes(filtroValor.toLowerCase());
    });
  });

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (setStartDate) {
      setStartDate(e.target.value);
    }
  };
  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (setEndDate) {
      setEndDate(e.target.value);
    }
  };

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(setListado(true));
  }, []);

  const versionMobile = useMediaQuery(theme.breakpoints.down('lg'));
  return (
    <Box className="listado" ml={{ xs: '15px', sm: '20px' }}>
      <Toolbar
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          height: onFiltrar ? { xs: '120px!important', lg: '70px !important' } : { xs: 'unset', lg: '70px !important' },
          py: onFiltrar && { xs: 2, lg: 1 },
          mt: { xs: 3, lg: 'unset' },
          gap: { xs: 2, lg: 0 },
          justifyContent: { xs: 'start', lg: 'end' },
        }}
      >
        {!!onFiltrar && (
          <Box
            maxWidth={anchoMaxForms}
            minWidth={{ xs: 'unset', lg: '700px' }}
            display="flex"
            gap="10px"
            component="form"
            onSubmit={onFiltrar}
          >
            {!versionMobile && (
              <Typography
                variant="h6"
                fontSize="1rem"
                textAlign="left"
                mb={2}
                pt={2}
                color={theme.palette.secondary.dark}
              >
                Filtrar por rango de fechas
              </Typography>
            )}
            <TextField
              InputLabelProps={{
                shrink: true,
              }}
              type="date"
              label="Fecha de inicio"
              variant="filled"
              size="small"
              value={startDate}
              onChange={handleStartDateChange}
            />
            <TextField
              InputLabelProps={{
                shrink: true,
              }}
              type="date"
              label="Fecha de fin"
              variant="filled"
              size="small"
              value={endDate}
              onChange={handleEndDateChange}
            />
            <Box display="flex" alignItems="normal" width="56px">
              <Tooltip title="Filtrar">
                <>
                  <span style={{ display: 'none' }}></span>
                  <IconButton
                    aria-label="filtrar"
                    type="submit"
                    sx={{ width: '100%' }}
                    disabled={!startDate || !endDate}
                  >
                    <FilterList />
                  </IconButton>
                </>
              </Tooltip>
            </Box>
            <Box display="flex" alignItems="normal" width="56px">
              <Tooltip title="Limpiar campos">
                <>
                  <span style={{ display: 'none' }}></span>
                  <IconButton
                    aria-label="resetear"
                    sx={{ width: '100%', ml: '-20px' }}
                    onClick={onLimpiarFechas}
                    disabled={!startDate || !endDate}
                  >
                    <ReplyAll />
                  </IconButton>
                </>
              </Tooltip>
            </Box>
          </Box>
        )}

        <Box display="flex" gap={4} alignItems="center">
          <Typography
            textAlign="center"
            variant="h5"
            component="div"
            sx={{ flex: '1 1 100%', color: theme.palette.secondary.main }}
            fontSize={{ xs: '1.2rem', md: '1.5rem' }}
          >
            {titulo}
          </Typography>
          {onAgregar && (
            <Tooltip
              children={
                <IconButton
                  onClick={onAgregar}
                  color="secondary"
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    color: 'white',
                    '&:hover': {
                      backgroundColor: theme.palette.secondary.main,
                    },
                  }}
                >
                  <AddIcon />
                </IconButton>
              }
              title="Agregar"
            />
          )}
        </Box>
      </Toolbar>
      {loading ? (
        <Spinner />
      ) : (
        <Paper>
          <TableContainer sx={{ maxHeight: '100%' }}>
            <Table stickyHeader>
              <TableHead className="tableheader">
                <TableRow>
                  {headCells.map(headCell => (
                    <Tooltip
                      key={headCell.Id as string}
                      arrow
                      placement="top"
                      slotProps={{
                        popper: {
                          modifiers: [
                            {
                              name: 'offset',
                              options: {
                                offset: [0, -20],
                              },
                            },
                          ],
                        },
                      }}
                      children={
                        <TableCell
                          align="center"
                          className="tableHead"
                          sx={{
                            px: 1,
                            maxWidth: 200,
                          }}
                        >
                          <Box display="flex" justifyContent="center" alignItems="center">
                            <IconButton
                              sx={{ height: '100%' }}
                              size="small"
                              onClick={() => handleCollapse(String(headCell.Id))}
                            >
                              {collapsedColumns.includes(String(headCell.Id)) ? <ArrowRight /> : <ArrowLeft />}
                            </IconButton>
                            <Collapse in={!collapsedColumns.includes(String(headCell.Id))} timeout="auto" unmountOnExit>
                              <Box display="flex">
                                <TextField
                                  variant="standard"
                                  type="search"
                                  size="small"
                                  placeholder="Buscar"
                                  label={collapsedColumns.includes(String(headCell.Id)) ? '' : headCell.label}
                                  onChange={e => setFiltros({ ...filtros, [headCell.Id as string]: e.target.value })}
                                  className="inputField"
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="end">
                                        <SavedSearchIcon
                                          sx={{
                                            fontSize: '1.4em',
                                            color: theme.palette.primary.main,
                                            mr: 1,
                                          }}
                                        />
                                      </InputAdornment>
                                    ),
                                    sx: {
                                      mx: { xs: 3, md: 'auto' },
                                    },
                                    disableUnderline: true,
                                  }}
                                />
                                <TableSortLabel
                                  active={orderBy === headCell.Id}
                                  direction={orderBy === headCell.Id ? order : 'asc'}
                                  onClick={() => handleRequestSort(headCell.Id)}
                                  sx={{
                                    '.MuiTableSortLabel-icon': {
                                      color: 'lightgray',
                                      opacity: 1,
                                      fontWeight: 'bold',
                                    },
                                  }}
                                />
                              </Box>
                            </Collapse>
                          </Box>
                        </TableCell>
                      }
                      title={headCell.label as string}
                    />
                  ))}
                  {(onEliminar || onEditar) && (
                    <TableCell
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        fontSize: '1rem',
                        fontWeight: 600,
                        border: 'none',
                        height: '100px',
                        padding: 0,
                        borderBottom: '1px solid rgba(224, 224, 224, 1)',
                      }}
                    >
                      Acciones
                      <AutoAwesomeIcon
                        sx={{
                          ml: 3,
                          minWidth: '100%',
                          maxWidth: { xs: '100%', lg: 120 },
                          mx: 'auto',
                        }}
                      />
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredList.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={headCells.length + (onSeleccionar ? 1 : 0) + (onEliminar || onEditar ? 1 : 0)}>
                      <Typography variant="h6" align="center" color="textSecondary">
                        No se encontraron resultados.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredList
                    .slice(page * filasPorPagina, page * filasPorPagina + filasPorPagina)
                    .map((row, index) => {
                      const seleccionado = isSelected(row.Id);
                      const srcAfiche = 'AficheUrl' in row ? row.AficheUrl : '';
                      const srcBanner = 'BannerUrl' in row ? row.BannerUrl : '';
                      const srcImagenBoletin = 'ImagenUrl' in row ? row.ImagenUrl : '';
                      const srcFicha = 'DetallePath' in row ? row.DetallePath : '';
                      const nombre = 'Nombre' in row ? row.Nombre : '';
                      const inscriptos = 'Programa' in row ? row.Programa : '';

                      return (
                        <StyledTableRow
                          hover
                          onClick={event => handleClick(event, row.Id)}
                          role="checkbox"
                          aria-checked={seleccionado}
                          tabIndex={-1}
                          key={`${row.Id}-${index}`}
                          selected={seleccionado}
                        >
                          {headCells.map(headCell => (
                            <TableCell
                              key={headCell.Id as string}
                              align="center"
                              sx={{
                                px: 1,
                                textOverflow: 'ellipsis',
                                overflowX: 'auto',
                                borderRight: '1px solid rgba(224, 224, 224, 1)',
                              }}
                            >
                              <Collapse
                                in={!collapsedColumns.includes(String(headCell.Id))}
                                timeout="auto"
                                unmountOnExit
                              >
                                {headCell.Id === 'AficheUrl' && srcAfiche !== '' ? (
                                  <MiniaturaFoto src={srcAfiche as string} />
                                ) : headCell.Id === 'BannerUrl' && srcBanner !== '' ? (
                                  <MiniaturaFoto src={srcBanner as string} />
                                ) : headCell.Id === 'ImagenUrl' && srcImagenBoletin !== '' ? (
                                  <MiniaturaFoto src={srcImagenBoletin as string} />
                                ) : headCell.Id === 'Nombre' ? (
                                  <Link component="button" onClick={() => navigate(`${srcFicha}`)}>
                                    {`${nombre}`}
                                  </Link>
                                ) : headCell.Id === 'Programa' ? (
                                  <Link component="button" onClick={() => navigate(`${srcFicha}`)}>
                                    {`${inscriptos}`}
                                  </Link>
                                ) : (headCell.Id === 'TipoSocio' ||
                                    headCell.Id === 'Rol' ||
                                    headCell.Id === 'VisibleEnGrilla' ||
                                    headCell.Id === 'BannerDestacado') &&
                                  (row as any)[headCell.Id] !== '-' ? (
                                  <Chip
                                    label={(row as any)[headCell.Id]}
                                    sx={{
                                      backgroundColor:
                                        (row as any)[headCell.Id] === 'Socio Total' ||
                                        (row as any)[headCell.Id] === 'No' ||
                                        (row as any)[headCell.Id] === 'Administrador'
                                          ? '#ffbc8b'
                                          : '#b2eff5',
                                    }}
                                  />
                                ) : (
                                  (row as any)[headCell.Id]
                                )}
                              </Collapse>
                            </TableCell>
                          ))}
                          <TableCell
                            sx={{ display: 'flex', justifyContent: 'center', borderBottom: 'none' }}
                            align="center"
                          >
                            {onEditar && (
                              <Tooltip title="Editar">
                                <IconButton onClick={() => onEditar(row.Id)} aria-label="Editar">
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                            {onEliminar && (
                              <Tooltip title="Eliminar">
                                <IconButton onClick={() => onEliminar(row.Id)} aria-label="Eliminar">
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </StyledTableRow>
                      );
                    })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 15, 25]}
            component="div"
            count={filteredList.length}
            rowsPerPage={filasPorPagina}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Elementos por página"
          />
        </Paper>
      )}
    </Box>
  );
};

export default Listado;
